import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { InsightsService } from '../services/insights.service';
import { EntityType, entityRoute } from '../shared/format';

interface Hit { group: string; ico: string; title: string; sub?: string; meta?: string; commands: any[]; queryParams?: any; }

const GROUP_LABEL: Record<string, string> = {
  brewery: 'Bares', event: 'Eventos', reservation: 'Reservas', customer: 'Clientes', user: 'Usuarios partner y admin',
  code: 'Códigos', affiliate: 'Afiliados',
};
const GROUP_ICO: Record<string, string> = { brewery: 'BAR', event: 'EVT', reservation: 'RES', customer: 'CLI', user: 'USR', code: 'COD', affiliate: 'AFI' };

/** Búsqueda global ⌘K (design.md §5.3): registros + acciones rápidas + ítems del menú. */
@Component({
  selector: 'app-search-dialog',
  template: `
  <p-dialog [(visible)]="visible" [modal]="true" [showHeader]="false" [dismissableMask]="true" [draggable]="false" [resizable]="false"
    [style]="{ width: '720px', maxWidth: '94vw' }" position="top" [contentStyle]="{ padding: '0', borderRadius: '14px' }" (onShow)="focus()">
    <div role="dialog" aria-label="Búsqueda global" (keydown)="onKey($event)">
      <div class="flex align-items-center gap-3" style="padding:16px 20px;border-bottom:1px solid var(--dh-line)">
        <i class="pi pi-search dh-muted" aria-hidden="true"></i>
        <label for="dh-global-search" class="sr-only">Buscar</label>
        <input #input id="dh-global-search" type="text" [(ngModel)]="q" (ngModelChange)="onQuery($event)" autocomplete="off"
          placeholder="Localizador, email, bar, evento o código…" style="flex:1;min-width:0;border:none;outline:none;font:inherit;font-size:18px;background:transparent;color:var(--dh-ink)">
        <span class="dh-kbd">Esc</span>
      </div>
      <div style="max-height:60vh;overflow-y:auto;padding:8px">
        <div *ngIf="loading" class="dh-muted text-sm" style="padding:10px 12px"><i class="pi pi-spin pi-spinner mr-2"></i>Buscando…</div>
        <ng-container *ngFor="let g of groups">
          <div class="dh-eyebrow" style="padding:10px 12px 4px">{{ g.title }}</div>
          <a *ngFor="let h of g.items" class="dh-search-hit" [class.on]="h === active" [routerLink]="h.commands" [queryParams]="h.queryParams" (click)="close()" (mouseenter)="active = h">
            <span class="dh-search-ico">{{ h.ico }}</span>
            <span style="flex:1;min-width:0;display:flex;flex-direction:column">
              <span class="font-semibold">{{ h.title }}</span>
              <span *ngIf="h.sub" class="text-xs dh-muted">{{ h.sub }}</span>
            </span>
            <span *ngIf="h.meta" class="text-xs dh-muted white-space-nowrap">{{ h.meta }}</span>
          </a>
        </ng-container>
        <div *ngIf="!loading && q.trim().length >= 2 && !hasRecords" class="dh-muted text-center" style="padding:24px 12px">
          Sin resultados para "{{ q }}". Prueba con un localizador, un email o el nombre de un bar.
        </div>
      </div>
      <div class="flex gap-3 flex-wrap text-xs dh-muted" style="padding:10px 20px;border-top:1px solid var(--dh-line)">
        <span><span class="dh-kbd">↑</span> <span class="dh-kbd">↓</span> moverse</span>
        <span><span class="dh-kbd">Enter</span> abrir</span>
        <span><span class="dh-kbd">{{ mod }}</span> <span class="dh-kbd">Enter</span> abrir en pestaña nueva</span>
      </div>
    </div>
  </p-dialog>`,
  styles: [`
    .sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}
    .dh-search-hit{display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:8px;color:var(--dh-ink);text-decoration:none}
    .dh-search-hit.on,.dh-search-hit:focus-visible{background:#F7EEE6;outline:none}
    .dh-search-ico{width:34px;height:34px;border-radius:8px;background:#F1E9E1;display:inline-flex;align-items:center;justify-content:center;flex:none;font-size:11px;font-weight:700;color:#7A3507}`],
})
export class AppSearchDialogComponent {
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  visible = false;
  q = '';
  loading = false;
  hasRecords = false;
  groups: { title: string; items: Hit[] }[] = [];
  active: Hit | null = null;
  mod = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
  private records: Hit[] = [];
  private query$ = new Subject<string>();

  private actions: Hit[] = [
    { group: 'Acciones', ico: '+', title: 'Nuevo evento', sub: 'Crear un evento desde cero', commands: ['/events/new'] },
    { group: 'Acciones', ico: '+', title: 'Duplicar evento…', sub: 'Elige un evento de la lista y una fecha nueva', commands: ['/events'] },
    { group: 'Acciones', ico: '+', title: 'Regalar entrada…', sub: 'Regalía para un cliente registrado', commands: ['/reservations'], queryParams: { gift: 1 } },
    { group: 'Acciones', ico: '+', title: 'Nuevo código de descuento', commands: ['/discount-codes/new'] },
    { group: 'Acciones', ico: '+', title: 'Generar liquidaciones', sub: 'Vista previa por sucursal', commands: ['/branch-settlements/generate'] },
    { group: 'Acciones', ico: '+', title: 'Nuevo usuario de partner', commands: ['/partner-users/new'] },
  ];
  private pages: Hit[] = [
    ['Resumen', '/'], ['Pendientes', '/pending'], ['Monitor en vivo', '/livemap'], ['Reservas', '/reservations'], ['Clientes', '/customers'],
    ['Visitas', '/visits'], ['Códigos', '/discount-codes'], ['Eventos', '/events'], ['Precios del recorrido', '/pricing'], ['Banners', '/banners'],
    ['Bares y sucursales', '/breweries'], ['Productos', '/products'], ['Usuarios partner', '/partner-users'], ['Feedback', '/feedback'],
    ['Liquidaciones', '/branch-settlements'], ['Afiliados', '/affiliates'], ['Comisiones', '/commissions'], ['Solicitudes de cobro', '/payout-requests'],
    ['Pagos a afiliados', '/payouts'], ['Facturación', '/billing'], ['Reporte a partners', '/reports'], ['Ingresos por día', '/income'], ['Configuración', '/product-category'],
  ].map(([title, path]) => ({ group: 'Ir a', ico: '→', title, commands: [path] }));

  constructor(private insights: InsightsService, private router: Router) {
    this.query$.pipe(
      debounceTime(220),
      distinctUntilChanged(),
      switchMap((q) => {
        if (q.trim().length < 2) return of({ results: [] });
        this.loading = true;
        return this.insights.search(q.trim()).pipe(catchError(() => of({ results: [] })));
      }),
    ).subscribe((res) => {
      this.loading = false;
      this.records = (res.results ?? []).map((r: any) => {
        const route = entityRoute(r.type as EntityType, r.id) ?? { commands: ['/'] };
        return { group: GROUP_LABEL[r.type] ?? 'Otros', ico: GROUP_ICO[r.type] ?? '•', title: r.title, sub: r.subtitle, meta: r.meta, commands: route.commands, queryParams: route.queryParams };
      });
      this.rebuild();
    });
  }

  open(): void {
    this.visible = true;
    this.q = '';
    this.records = [];
    this.rebuild();
  }

  close(): void { this.visible = false; }
  focus(): void { setTimeout(() => this.input?.nativeElement.focus(), 0); }

  onQuery(q: string): void {
    this.rebuild();
    this.query$.next(q);
  }

  private rebuild(): void {
    const term = this.q.trim().toLowerCase();
    const match = (h: Hit) => !term || h.title.toLowerCase().includes(term) || (h.sub ?? '').toLowerCase().includes(term);
    const order = ['Bares', 'Eventos', 'Reservas', 'Clientes', 'Usuarios partner y admin', 'Códigos', 'Afiliados'];
    const groups = order.map((t) => ({ title: t, items: this.records.filter((r) => r.group === t) })).filter((g) => g.items.length);
    this.hasRecords = groups.length > 0;
    const actions = this.actions.filter(match);
    const pages = term ? this.pages.filter(match).slice(0, 5) : [];
    if (actions.length) groups.push({ title: 'Acciones', items: term ? actions : actions.slice(0, 4) });
    if (pages.length) groups.push({ title: 'Ir a', items: pages });
    this.groups = groups;
    this.active = groups[0]?.items[0] ?? null;
  }

  onKey(e: KeyboardEvent): void {
    const flat = this.groups.flatMap((g) => g.items);
    if (!flat.length) return;
    const i = this.active ? flat.indexOf(this.active) : -1;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.active = flat[Math.min(flat.length - 1, i + 1)]; }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.active = flat[Math.max(0, i - 1)]; }
    else if (e.key === 'Enter' && this.active) {
      e.preventDefault();
      const tree = this.router.createUrlTree(this.active.commands, { queryParams: this.active.queryParams });
      if (e.metaKey || e.ctrlKey) window.open(this.router.serializeUrl(tree), '_blank');
      else { this.router.navigateByUrl(tree); this.close(); }
    }
  }
}
